import {
  Component,
  DOCUMENT,
  ElementRef,
  Injector,
  afterNextRender,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { IndicatorCircleComponent } from 'app/organization/organization/org-management/shared/indicator-circle/indicator-circle.component';
import { AgentInputBar } from 'app/shared/agent/ui/agent-input-bar/agent-input-bar';
import { MenuItem } from 'app/shared/models/menu/menu-item.model';
import { NavtreeItem } from 'app/shared/models/navtree.model';
import { AppState } from 'app/state/app.state';
import { selectCurrentGroup } from 'app/state/selectors/user-state.selectors';
import {
  FormaAttention,
  FormaAttentionVariant,
  FormaButton,
  FormaButtonGroup,
  FormaIcon,
  FormaIconButton,
  FormaMenu,
  FormaMenuItem,
  FormaMenuTrigger,
} from 'forma';
import { AgentChatThread } from '../../agent/ui/agent-chat-thread/agent-chat-thread';
import { CoachBounds, CoachStore } from '../coach.store';

/** The layout's scroll container that holds the report page, right of the menu and below the header. */
const PAGE_AREA_SELECTOR = '.insight-container';
/** The report content inside the page area; it has a max width, so a wide screen has free space right of it. */
const PAGE_CONTENT_SELECTOR = '.pages';
/** The app's top bar, which overlays the page area; the maximized window stays clear of it. */
const TOP_BAR_SELECTOR = 'app-header';
/** The fade below the top bar; page content starts below it too. */
const TOP_BAR_GAP = 16;
/** Space kept to the bottom of the screen. */
const BOTTOM_GAP = 16;
const MIN_WINDOW_WIDTH = 320;
const MIN_WINDOW_HEIGHT = 240;
const VIEWPORT_GAP = 16;
const WIDE_VIEWPORT = 1200;
const DRAG_THRESHOLD = 4;
/** Matches `.coach-header`'s and `.coach-button-group`'s gaps in coach-window.scss. */
const HEADER_GAP = 8;
const HEADER_BUTTON_GAP = 8;
/** Docked right, the window fills the free space right of the report content within these limits. */
const RIGHT_DOCK_MIN_FIT = 420;
const RIGHT_DOCK_MAX_FIT = 1200;
const RIGHT_DOCK_CONTENT_GAP = 8;
/** Without enough free space: 40% of the screen, within these limits. */
const RIGHT_DOCK_DEFAULT_SHARE = 0.4;
const RIGHT_DOCK_DEFAULT_MAX = 788;

type CoachResizeEdge =
  'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface CoachResizeOrigin {
  edge: CoachResizeEdge;
  pointerX: number;
  pointerY: number;
  bounds: CoachBounds;
}

@Component({
  selector: 'app-coach-window',
  templateUrl: './coach-window.html',
  styleUrl: './coach-window.scss',
  imports: [
    AgentChatThread,
    AgentInputBar,
    FormaAttention,
    FormaButton,
    FormaButtonGroup,
    FormaIcon,
    FormaIconButton,
    FormaMenu,
    FormaMenuItem,
    FormaMenuTrigger,
    IndicatorCircleComponent,
    TranslocoPipe,
  ],
})
export class CoachWindow {
  protected readonly coach = inject(CoachStore);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly appStore = inject(Store<AppState>);
  private readonly userInfo = this.appStore.selectSignal((state) => state.userInfo);
  private readonly company = this.appStore.selectSignal((state) => state.companyContext);
  private readonly userState = this.appStore.selectSignal((state) => state.userState);
  private readonly pageKey = this.appStore.selectSignal(
    (state) => state.reportState?.activeReportPage?.key ?? null,
  );
  protected readonly currentGroup = this.appStore.selectSignal(selectCurrentGroup);

  /** The report pages the menu shows: the menu has already evaluated their conditions for this user. */
  private readonly visiblePageKeys = this.appStore.selectSignal((state) =>
    visibleMenuKeys(state.navtreeState?.items ?? []),
  );
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly composer = viewChild(AgentInputBar);
  private readonly expandButton = viewChild<unknown, ElementRef<HTMLButtonElement>>(
    'expandButton',
    {
      read: ElementRef,
    },
  );
  private readonly confirmResetButton = viewChild<unknown, ElementRef<HTMLButtonElement>>(
    'confirmResetButton',
    {
      read: ElementRef,
    },
  );
  private readonly newConversationButton = viewChild<unknown, ElementRef<HTMLButtonElement>>(
    'newConversationButton',
    {
      read: ElementRef,
    },
  );
  private readonly menuTrigger = viewChild<unknown, ElementRef<HTMLButtonElement>>('menuTrigger', {
    read: ElementRef,
  });
  private readonly header = viewChild<ElementRef<HTMLElement>>('header');
  private readonly title = viewChild<ElementRef<HTMLElement>>('title');
  private readonly headerWidth = signal(0);
  /** The header actions move into a menu once they and the title no longer fit on one line. */
  protected readonly compactHeader = signal(false);
  private focusRequested = false;
  private dragOrigin: { pointerX: number; pointerY: number; bounds: CoachBounds } | null = null;
  private resizeOrigin: CoachResizeOrigin | null = null;
  private readonly viewportWidth = signal(0);
  protected readonly dragging = signal(false);
  protected readonly resizing = signal(false);
  /** Docked at the bottom, only the free top side resizes; a floating window resizes everywhere. */
  protected readonly resizeCorners = computed(() =>
    this.dockedBottom()
      ? (['top-left', 'top-right'] as const)
      : (['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const),
  );
  protected readonly resizeEdges = computed(() =>
    this.dockedBottom() ? (['top'] as const) : (['top', 'bottom'] as const),
  );

  /** "Ny konversation" asks once before the conversation is cleared. */
  protected readonly confirmingReset = signal(false);
  /** The composer's own attention: it turns while the Coach answers and fades out after. */
  protected readonly composerAttention = signal<FormaAttentionVariant | null>(null);
  protected readonly dockedRight = computed(
    () =>
      this.coach.layout() === 'right' ||
      (this.coach.layout() === null && this.viewportWidth() >= WIDE_VIEWPORT),
  );
  protected readonly dockedBottom = computed(
    () =>
      this.coach.layout() === 'bottom' ||
      (this.coach.layout() === null && this.viewportWidth() < WIDE_VIEWPORT),
  );
  /** A docked window can't be moved; it is resized only along its free sides. */
  protected readonly docked = computed(() => this.dockedRight() || this.dockedBottom());
  protected readonly floatingStyle = computed(() => {
    const storedBounds = this.coach.bounds();
    if (this.coach.layout() !== 'floating' || !storedBounds) return null;
    const bounds = this.clampBounds(storedBounds);
    return {
      top: `${bounds.top}px`,
      left: `${bounds.left}px`,
      width: `${bounds.width}px`,
      height: `${bounds.height}px`,
      right: 'auto',
      bottom: 'auto',
    };
  });
  protected readonly panelStyle = computed(() => {
    if (this.coach.maximized()) return this.maximizedStyle();
    if (this.coach.layout() === 'floating') return this.floatingStyle();
    const area = this.pageArea();
    if (this.dockedRight()) {
      return {
        ...(area
          ? {
              top: `${area.top}px`,
              height: `${area.height}px`,
              bottom: 'auto',
            }
          : {}),
        ...(this.rightDockWidth() ? { width: `${this.rightDockWidth()}px` } : {}),
        ...(this.contentDock() ? { left: `${this.contentDock()!.left}px`, right: 'auto' } : {}),
      };
    }
    const { bottomLeft, bottomWidth, bottomHeight } = this.coach.dockSize();
    const style = {
      ...(bottomLeft !== null && bottomWidth !== null
        ? { left: `${bottomLeft}px`, width: `${bottomWidth}px`, right: 'auto' }
        : {}),
      ...(bottomHeight !== null ? { height: `${bottomHeight}px` } : {}),
    };
    return Object.keys(style).length ? style : null;
  });

  /**
   * With enough free space right of the report content, the window docks against the content
   * (left edge fixed) at up to the maximum width. Otherwise it docks to the screen edge at a
   * dragged width or a default share of the screen.
   */
  private readonly contentDock = computed(() => {
    const contentRight = this.contentRight();
    const area = this.pageArea();
    if (contentRight === null || !area) return null;
    const left = Math.ceil(contentRight + RIGHT_DOCK_CONTENT_GAP);
    const free = Math.floor(area.left + area.width - left);
    return free >= RIGHT_DOCK_MIN_FIT ? { left, free } : null;
  });
  protected readonly dockedToContent = computed(
    () => this.dockedRight() && this.contentDock() !== null,
  );
  private readonly rightDockWidth = computed(() => {
    const dragged = this.coach.dockSize().rightWidth;
    const contentDock = this.contentDock();
    if (contentDock) {
      return Math.min(dragged ?? RIGHT_DOCK_MAX_FIT, RIGHT_DOCK_MAX_FIT, contentDock.free);
    }
    if (dragged) return dragged;
    const viewport = this.viewportWidth();
    if (!viewport) return null;
    const share = Math.min(
      Math.max(viewport * RIGHT_DOCK_DEFAULT_SHARE, RIGHT_DOCK_MIN_FIT),
      RIGHT_DOCK_DEFAULT_MAX,
    );
    return Math.floor(Math.min(share, viewport - VIEWPORT_GAP * 2));
  });
  /** Right edge of the report content on screen; null without report content. */
  private readonly contentRight = signal<number | null>(null);

  /** The visible part of the report page area (right of the menu, below the top bar), while maximized. */
  private readonly pageArea = signal<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  protected readonly maximizedStyle = computed(() => {
    const area = this.pageArea();
    if (!area) return null;
    return {
      top: `${area.top}px`,
      left: `${area.left}px`,
      width: `${area.width}px`,
      height: `${area.height}px`,
      right: 'auto',
      bottom: 'auto',
    };
  });

  /**
   * Where the page content sits on screen: the page area inside its padding (the top padding
   * keeps content clear of the overlaying top bar), clipped to the screen and never above the
   * top bar, also when the whole document has scrolled.
   */
  private visiblePageArea(area: HTMLElement, view: Window) {
    const box = area.getBoundingClientRect();
    const padding = view.getComputedStyle(area);
    const topBar = this.document.querySelector(TOP_BAR_SELECTOR)?.getBoundingClientRect().bottom;
    const top = Math.max(
      box.top + parseFloat(padding.paddingTop || '0'),
      topBar === undefined ? 0 : topBar + TOP_BAR_GAP,
      0,
    );
    const bottom = Math.min(
      box.bottom - parseFloat(padding.paddingBottom || '0'),
      view.innerHeight - BOTTOM_GAP,
    );
    const left = box.left + parseFloat(padding.paddingLeft || '0');
    const width =
      box.width - parseFloat(padding.paddingLeft || '0') - parseFloat(padding.paddingRight || '0');
    return { top, left, width, height: Math.max(bottom - top, 0) };
  }

  constructor() {
    effect(() => {
      if (this.coach.busy()) untracked(() => this.composerAttention.set('modern'));
    });

    effect((onCleanup) => {
      const view = this.document.defaultView;
      if (!view) return;
      const updateViewportWidth = () => this.viewportWidth.set(view.innerWidth);
      updateViewportWidth();
      view.addEventListener('resize', updateViewportWidth);
      onCleanup(() => view.removeEventListener('resize', updateViewportWidth));
    });

    // Maximized, the window covers the visible report page area and follows it when the page
    // scrolls, the window is resized or the menu is collapsed. Without that area it falls back
    // to the CSS size. Docked right, it spans the page area's height and measures the free space
    // right of the report content.
    effect((onCleanup) => {
      const view = this.document.defaultView;
      const area = this.document.querySelector<HTMLElement>(PAGE_AREA_SELECTOR);
      if (
        !this.coach.active() ||
        this.coach.minimized() ||
        (!this.coach.maximized() && !this.dockedRight()) ||
        !view ||
        !area
      ) {
        this.pageArea.set(null);
        this.contentRight.set(null);
        return;
      }
      const observer = new ResizeObserver(() => measure());
      const measure = () => {
        // Navigation swaps the routed page; watch whatever is there now.
        for (const child of Array.from(area.children)) observer.observe(child);
        const content = area.querySelector<HTMLElement>(PAGE_CONTENT_SELECTOR);
        if (content) observer.observe(content);
        const contentBox = content?.getBoundingClientRect();
        const right = contentBox && contentBox.width > 0 ? contentBox.right : null;
        if (
          right === null ||
          this.contentRight() === null ||
          Math.abs(right - this.contentRight()!) >= 0.5
        )
          this.contentRight.set(right);

        const next = this.visiblePageArea(area, view);
        const current = this.pageArea();
        // Only a real change: re-sizing the window makes the thread jump to its newest message.
        if (
          current &&
          (['top', 'left', 'width', 'height'] as const).every(
            (k) => Math.abs(current[k] - next[k]) < 0.5,
          )
        )
          return;
        this.pageArea.set(next);
      };
      // Scrolling the conversation itself doesn't move the page area.
      const onScroll = (event: Event) => {
        if (event.target instanceof Node && this.panel()?.nativeElement.contains(event.target))
          return;
        measure();
      };
      untracked(measure);
      observer.observe(area);
      view.addEventListener('resize', measure);
      view.addEventListener('scroll', onScroll, { capture: true, passive: true });
      onCleanup(() => {
        observer.disconnect();
        view.removeEventListener('resize', measure);
        view.removeEventListener('scroll', onScroll, { capture: true });
      });
    });

    effect(() => {
      const companyId = this.company()?.id;
      const userId = this.userInfo()?.userId;
      const userState = this.userState();
      if (!companyId || !userId) {
        this.coach.setContext(null);
        return;
      }
      if (
        userState?.currentCompanyId !== companyId ||
        (userState.userId && userState.userId !== userId)
      ) {
        this.coach.setContext(null);
        return;
      }
      const parameters = userState.companyParameters?.find((item) => item.companyId === companyId);
      const groupId = Number(
        parameters?.parameters?.find((item) => item.name === 'currentGroup')?.value,
      );
      if (Number.isSafeInteger(groupId) && groupId > 0) {
        this.coach.setContext({
          companyId,
          userId,
          groupId,
          productContextId: this.company()?.productContext?.id ?? null,
          pageKey: this.pageKey(),
          visiblePageKeys: this.visiblePageKeys(),
        });
      } else {
        this.coach.setContext(null);
      }
    });

    afterRenderEffect((onCleanup) => {
      const panel = this.panel()?.nativeElement;
      const origin = this.coach.origin();
      const minimized = this.coach.minimized();
      if (this.coach.focusRequested() || this.focusRequested) {
        const target = minimized ? this.expandButton()?.nativeElement : this.composer();
        if (target) {
          target.focus();
          this.focusRequested = false;
          this.coach.finishFocus();
        }
      }
      if (!panel || !origin || minimized) return;
      if (this.document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.coach.finishAnimation();
        this.composer()?.focus();
        return;
      }

      const target = panel.getBoundingClientRect();
      const animation = panel.animate(
        [
          {
            left: `${origin.left}px`,
            top: `${origin.top}px`,
            width: `${origin.width}px`,
            height: `${origin.height}px`,
            borderRadius: '32px',
            margin: '0',
            right: 'auto',
            bottom: 'auto',
          },
          {
            left: `${target.left}px`,
            top: `${target.top}px`,
            width: `${target.width}px`,
            height: `${target.height}px`,
            borderRadius: '16px',
            margin: '0',
            right: 'auto',
            bottom: 'auto',
          },
        ],
        { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
      animation.onfinish = () => {
        this.coach.finishAnimation();
        this.composer()?.focus();
      };
      onCleanup(() => animation.cancel());
    });

    afterRenderEffect((onCleanup) => {
      const header = this.header()?.nativeElement;
      if (!header) return;
      const observer = new ResizeObserver(() => this.headerWidth.set(header.clientWidth));
      observer.observe(header);
      onCleanup(() => observer.disconnect());
    });

    afterRenderEffect(() => {
      const width = this.headerWidth();
      const header = this.header()?.nativeElement;
      const title = this.title()?.nativeElement;
      const showsNewConversation = this.coach.messages().length > 0 && !this.confirmingReset();
      this.currentGroup();
      if (!header || !title || !width) return;
      const avatar = header.querySelector<HTMLElement>('.coach-avatar')?.offsetWidth ?? 0;
      const button = header.querySelector<HTMLElement>('.coach-minimize')?.offsetWidth ?? 0;
      const buttons = 4 + Number(showsNewConversation);
      const range = this.document.createRange();
      range.selectNodeContents(title);
      const needed =
        avatar +
        range.getBoundingClientRect().width +
        buttons * button +
        (buttons - 1) * HEADER_BUTTON_GAP +
        HEADER_GAP * 2;
      this.compactHeader.set(needed > width);
    });

    afterRenderEffect((onCleanup) => {
      const panel = this.panel()?.nativeElement;
      if (!panel || this.coach.layout() !== 'floating' || this.coach.maximized()) return;
      const observer = new ResizeObserver(() => {
        const bounds = this.clampBounds(panel.getBoundingClientRect());
        if (!this.sameBounds(bounds, this.coach.bounds())) this.coach.move(bounds);
      });
      observer.observe(panel);
      onCleanup(() => observer.disconnect());
    });
  }

  protected minimize(): void {
    this.focusRequested = true;
    this.coach.minimize();
  }

  protected expand(): void {
    this.focusRequested = true;
    this.coach.expand();
  }

  protected dockRight(): void {
    if (this.dockedRight()) this.undock();
    else this.coach.dock('right');
  }

  protected dockBottom(): void {
    if (this.dockedBottom()) this.undock();
    else this.coach.dock('bottom');
  }

  /** The lit dock button floats the window where it is now. */
  private undock(): void {
    const panel = this.panel()?.nativeElement;
    if (!panel) return;
    const { top, left, width, height } = panel.getBoundingClientRect();
    this.coach.move(this.clampBounds({ top, left, width, height }));
  }

  protected startDrag(event: PointerEvent): void {
    if (event.button !== 0 || (event.target instanceof Element && event.target.closest('button')))
      return;
    const panel = this.panel()?.nativeElement;
    if (!panel || this.coach.maximized() || this.docked()) return;
    const bounds = panel.getBoundingClientRect();
    this.dragging.set(false);
    this.dragOrigin = { pointerX: event.clientX, pointerY: event.clientY, bounds };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  protected drag(event: PointerEvent): void {
    if (!this.dragOrigin) return;
    if ((event.buttons & 1) === 0) {
      this.endDrag();
      return;
    }
    const { pointerX, pointerY, bounds } = this.dragOrigin;
    const deltaX = event.clientX - pointerX;
    const deltaY = event.clientY - pointerY;
    if (!this.dragging() && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;
    this.dragging.set(true);
    this.coach.move(
      this.clampBounds({
        top: bounds.top + deltaY,
        left: bounds.left + deltaX,
        width: bounds.width,
        height: bounds.height,
      }),
    );
  }

  protected endDrag(): void {
    this.dragOrigin = null;
    this.dragging.set(false);
  }

  protected startResize(event: PointerEvent, edge: CoachResizeEdge): void {
    if (event.button !== 0 || this.coach.maximized()) return;
    const panel = this.panel()?.nativeElement;
    if (!panel) return;
    event.stopPropagation();
    this.resizeOrigin = {
      edge,
      pointerX: event.clientX,
      pointerY: event.clientY,
      bounds: panel.getBoundingClientRect(),
    };
    this.resizing.set(true);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  protected resize(event: PointerEvent): void {
    if (!this.resizeOrigin) return;
    if ((event.buttons & 1) === 0) {
      this.endResize();
      return;
    }
    const view = this.document.defaultView;
    if (!view) return;
    const { edge, pointerX, pointerY, bounds } = this.resizeOrigin;
    const deltaX = event.clientX - pointerX;
    const minimumLeft = VIEWPORT_GAP;
    const maximumRight = view.innerWidth - VIEWPORT_GAP;
    const right = bounds.left + bounds.width;

    if (edge !== 'left' && edge !== 'right') {
      // A floating window has all of these handles; the bottom dock only the top ones.
      const deltaY = event.clientY - pointerY;
      const bottom = bounds.top + bounds.height;
      const left = edge.endsWith('left')
        ? Math.min(Math.max(bounds.left + deltaX, minimumLeft), right - MIN_WINDOW_WIDTH)
        : bounds.left;
      const nextRight = edge.endsWith('right')
        ? Math.min(Math.max(right + deltaX, bounds.left + MIN_WINDOW_WIDTH), maximumRight)
        : right;
      const top = edge.startsWith('top')
        ? Math.min(Math.max(bounds.top + deltaY, this.minimumTop()), bottom - MIN_WINDOW_HEIGHT)
        : bounds.top;
      const nextBottom = edge.startsWith('bottom')
        ? Math.min(
            Math.max(bottom + deltaY, bounds.top + MIN_WINDOW_HEIGHT),
            view.innerHeight - VIEWPORT_GAP,
          )
        : bottom;
      if (this.dockedBottom()) {
        // The bottom stays docked; the top edge and corners change the height (and width).
        this.coach.resizeBottomDock(left, nextRight - left, nextBottom - top);
        return;
      }
      this.coach.move(
        this.clampBounds({ top, left, width: nextRight - left, height: nextBottom - top }),
      );
      return;
    }

    if (this.coach.layout() === 'floating') {
      const next =
        edge === 'left'
          ? {
              ...bounds,
              left: Math.min(Math.max(bounds.left + deltaX, minimumLeft), right - MIN_WINDOW_WIDTH),
              width: bounds.width,
            }
          : {
              ...bounds,
              width: Math.min(
                Math.max(bounds.width + deltaX, MIN_WINDOW_WIDTH),
                maximumRight - bounds.left,
              ),
            };
      if (edge === 'left') next.width = right - next.left;
      this.coach.move(this.clampBounds(next));
      return;
    }

    const contentDock = this.contentDock();
    if (this.dockedRight() && contentDock) {
      this.coach.resizeRightDock(
        Math.min(
          Math.max(bounds.width + deltaX, MIN_WINDOW_WIDTH),
          contentDock.free,
          RIGHT_DOCK_MAX_FIT,
        ),
      );
      return;
    }
    if (this.dockedRight()) {
      this.coach.resizeRightDock(
        Math.min(Math.max(bounds.width - deltaX, MIN_WINDOW_WIDTH), maximumRight - minimumLeft),
      );
      return;
    }
    if (edge === 'left') {
      const left = Math.min(Math.max(bounds.left + deltaX, minimumLeft), right - MIN_WINDOW_WIDTH);
      this.coach.resizeBottomDock(left, right - left);
    } else {
      const width = Math.min(
        Math.max(bounds.width + deltaX, MIN_WINDOW_WIDTH),
        maximumRight - bounds.left,
      );
      this.coach.resizeBottomDock(bounds.left, width);
    }
  }

  protected endResize(): void {
    this.resizeOrigin = null;
    this.resizing.set(false);
  }

  protected onEscape(event: Event): void {
    event.stopPropagation();
    if (this.confirmingReset()) {
      this.cancelReset();
      return;
    }
    this.minimize();
  }

  protected askReset(): void {
    this.confirmingReset.set(true);
    afterNextRender(() => this.confirmResetButton()?.nativeElement.focus(), {
      injector: this.injector,
    });
  }

  protected cancelReset(): void {
    this.confirmingReset.set(false);
    afterNextRender(
      () => (this.newConversationButton() ?? this.menuTrigger())?.nativeElement.focus(),
      {
        injector: this.injector,
      },
    );
  }

  protected async confirmReset(): Promise<void> {
    this.confirmingReset.set(false);
    await this.coach.newConversation();
    this.composer()?.focus();
  }

  private clampBounds(bounds: CoachBounds): CoachBounds {
    const view = this.document.defaultView;
    if (!view) return bounds;
    const minimumTop = this.minimumTop();
    const availableWidth = Math.max(view.innerWidth - VIEWPORT_GAP * 2, MIN_WINDOW_WIDTH);
    const width = Math.min(Math.max(bounds.width, MIN_WINDOW_WIDTH), availableWidth);
    const availableHeight = Math.max(
      view.innerHeight - minimumTop - VIEWPORT_GAP,
      MIN_WINDOW_HEIGHT,
    );
    const height = Math.min(Math.max(bounds.height, MIN_WINDOW_HEIGHT), availableHeight);
    const maximumTop = Math.max(view.innerHeight - height - VIEWPORT_GAP, minimumTop);
    return {
      top: Math.min(Math.max(bounds.top, minimumTop), maximumTop),
      left: Math.min(Math.max(bounds.left, VIEWPORT_GAP), view.innerWidth - width - VIEWPORT_GAP),
      width,
      height,
    };
  }

  private minimumTop(): number {
    const headerBottom = this.document
      .querySelector(TOP_BAR_SELECTOR)
      ?.getBoundingClientRect().bottom;
    return Math.max(
      headerBottom === undefined ? VIEWPORT_GAP : headerBottom + TOP_BAR_GAP,
      VIEWPORT_GAP,
    );
  }

  private sameBounds(left: CoachBounds, right: CoachBounds | null): boolean {
    return (
      right !== null &&
      (['top', 'left', 'width', 'height'] as const).every(
        (key) => Math.abs(left[key] - right[key]) < 0.5,
      )
    );
  }
}

function visibleMenuKeys(navtree: NavtreeItem[]): string[] {
  const keys: string[] = [];
  const walk = (items: MenuItem[] | undefined) => {
    for (const item of items ?? []) {
      if (!item.visible) continue;
      if (item.key) keys.push(item.key);
      walk(item.children);
    }
  };
  for (const section of navtree) walk(section.items);
  return keys;
}
